
import React, { createContext, useContext, useState, useEffect } from 'react';
import { storage } from '@/lib/storage';

type CurrencyType = 'GC' | 'SC';

// Available color themes for the customer app
export type ColorTheme = 'casino' | 'dark' | 'light';

export const COLOR_THEMES: { value: ColorTheme; label: string; description: string }[] = [
  { value: 'casino', label: 'Casino', description: 'Classic casino dark with gold & green accents' },
  { value: 'dark', label: 'Dark', description: 'True dark — near-black AMOLED-level backgrounds' },
  { value: 'light', label: 'Light', description: 'Bright white backgrounds with rich accents' },
];

const formatNumber = (num: number): string => {
  // Ensure num is a valid number
  const validNum = typeof num === 'number' && !isNaN(num) ? num : 0;
  
  if (validNum >= 1e9) return (validNum / 1e9).toFixed(1) + 'B';
  if (validNum >= 1e6) return (validNum / 1e6).toFixed(1) + 'M';
  if (validNum >= 1e3) return (validNum / 1e3).toFixed(1) + 'K';
  return validNum.toString();
};

const formatCurrency = (amount: number, _type: CurrencyType): string => {
  // Ensure amount is a valid number, default to 0 if not
  const numAmount = typeof amount === 'number' && !isNaN(amount) ? amount : 0;
  
  // Only abbreviate at 1 billion+
  if (numAmount >= 1e12) {
    return (numAmount / 1e12).toFixed(2) + 'T';
  }
  if (numAmount >= 1e9) {
    return (numAmount / 1e9).toFixed(2) + 'B';
  }
  
  // For numbers under 1 billion, show full number with commas and 2 decimals
  return numAmount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

interface ThemeContextType {
  selectedCurrency: CurrencyType;
  setSelectedCurrency: (currency: CurrencyType) => void;
  formatCurrency: (amount: number, type: CurrencyType) => string;
  getThemeColors: () => {
    primary: string;
    primaryForeground: string;
    gradient: string;
    shadow: string;
  };
  // Color theme management
  colorTheme: ColorTheme;
  setColorTheme: (theme: ColorTheme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [selectedCurrency, setSelectedCurrency] = useState<CurrencyType>(() => {
    // Initialize from storage if available
    const stored = storage.getItem('selectedCurrency');
    return (stored === 'SC' || stored === 'GC') ? stored : 'GC';
  });

  // Initialize color theme from storage or default to casino
  const [colorTheme, setColorThemeState] = useState<ColorTheme>(() => {
    const saved = storage.getItem('customer-color-theme');
    if (saved === 'casino' || saved === 'dark' || saved === 'light') return saved;
    return 'casino';
  });

  // Wrapper to persist color theme to storage
  const setColorTheme = (theme: ColorTheme) => {
    setColorThemeState(theme);
    storage.setItem('customer-color-theme', theme);
  };

  // Persist currency changes to storage
  useEffect(() => {
    storage.setItem('selectedCurrency', selectedCurrency);
  }, [selectedCurrency]);

  const getThemeColors = () => {
    if (selectedCurrency === 'GC') {
      return {
        primary: 'hsl(var(--casino-gold))',
        primaryForeground: 'hsl(var(--background))',
        gradient: 'var(--gradient-gold)',
        shadow: 'var(--shadow-gold)',
      };
    } else {
      return {
        primary: 'hsl(var(--casino-green))',
        primaryForeground: 'hsl(var(--background))',
        gradient: 'var(--gradient-green)',
        shadow: 'var(--shadow-glow)',
      };
    }
  };

  // Apply theme to document body when currency changes
  useEffect(() => {
    const body = document.body;
    if (selectedCurrency === 'GC') {
      body.setAttribute('data-theme', 'gold');
      body.classList.remove('theme-green');
      body.classList.add('theme-gold');
    } else {
      body.setAttribute('data-theme', 'green');
      body.classList.remove('theme-gold');
      body.classList.add('theme-green');
    }
  }, [selectedCurrency]);

  // Apply color theme to document body
  useEffect(() => {
    const body = document.body;
    // Remove all color theme classes
    const themeClasses = [
      'color-theme-casino', 'color-theme-dark', 'color-theme-light',
    ];
    body.classList.remove(...themeClasses);
    // Add the current color theme class (casino uses :root base styles)
    if (colorTheme !== 'casino') {
      body.classList.add(`color-theme-${colorTheme}`);
    }
  }, [colorTheme]);

  return (
    <ThemeContext.Provider value={{ 
      selectedCurrency, 
      setSelectedCurrency, 
      formatCurrency,
      getThemeColors,
      colorTheme,
      setColorTheme,
    }}>
      {children}
    </ThemeContext.Provider>
  );
};
